import type { ScheduleComponent } from '@syncfusion/ej2-react-schedule'
import type { IPatientSchedule } from 'features/Home/interfaces/IInfoSchedule'
import { type RefObject, useEffect, useRef, useState } from 'react'
import { type AppointmentColors, applyAppointmentColors } from './applyAppointmentColors'
import {
  countScheduleEventChanges,
  diffScheduleEvents,
  fingerprintSchedulePositions,
  isSameSchedulePosition,
  needsScheduleCollectionRefresh,
  patchLiveScheduleEvents,
  patchScheduleEngineEvents,
  removeScheduleEngineEvents,
  removeScheduleEventsById,
  toScheduleEventId,
} from './diffScheduleEvents'
import { canRefreshScheduleEvents, safeRefreshScheduleEvents } from './safeRefreshScheduleEvents'
import { scheduleEventStore } from './scheduleEventStore'

const WHOLESALE_CHANGE_RATIO = 0.5

const applyColors = (
  scheduleElement: HTMLElement,
  event: IPatientSchedule,
  getColors: (event: IPatientSchedule) => AppointmentColors,
) => {
  const colors = getColors(event)
  scheduleElement
    .querySelectorAll<HTMLElement>(
      `[data-id="Appointment_${event.id}"]:not(.e-schedule-event-clone)`,
    )
    .forEach((element) => applyAppointmentColors(element, colors))
}

const getRenderedEventsById = (schedule: ScheduleComponent): Map<number, IPatientSchedule> => {
  try {
    return new Map(
      schedule.getEvents().map((event) => [toScheduleEventId(event.id), event as IPatientSchedule]),
    )
  } catch {
    return new Map()
  }
}

const isTabVisible = () => typeof document === 'undefined' || document.visibilityState === 'visible'

type UseSilentEventSyncParams = {
  scheduleObj: RefObject<ScheduleComponent | null>
  data: IPatientSchedule[]
  getEventColors: (event: IPatientSchedule) => AppointmentColors
  instanceKey?: string
  onPatched?: () => void
}

export const useSilentEventSync = ({
  scheduleObj,
  data,
  getEventColors,
  instanceKey,
  onPatched,
}: UseSilentEventSyncParams): IPatientSchedule[] => {
  const [dataSource, setDataSource] = useState<IPatientSchedule[]>(data)
  const appliedRef = useRef<IPatientSchedule[] | null>(null)
  const dataRef = useRef(data)
  dataRef.current = data
  const dataSourceRef = useRef<IPatientSchedule[]>(dataSource)
  dataSourceRef.current = dataSource
  const paintedPositionsRef = useRef('')
  const getColorsRef = useRef(getEventColors)
  getColorsRef.current = getEventColors
  const onPatchedRef = useRef(onPatched)
  onPatchedRef.current = onPatched
  const instanceKeyRef = useRef(instanceKey)
  const paintRetryRafRef = useRef<number | undefined>(undefined)

  if (instanceKeyRef.current !== instanceKey) {
    instanceKeyRef.current = instanceKey
    appliedRef.current = null
    paintedPositionsRef.current = ''
    setDataSource(data)
  }

  const paintPositions = (events: IPatientSchedule[]) => {
    const schedule = scheduleObj.current
    if (!isTabVisible()) return false
    if (!canRefreshScheduleEvents(schedule)) return false

    const { missingIds } = patchScheduleEngineEvents(schedule, events)
    const refreshed =
      missingIds.length > 0
        ? safeRefreshScheduleEvents(schedule, true)
        : safeRefreshScheduleEvents(schedule)
    if (!refreshed) return false

    paintedPositionsRef.current = fingerprintSchedulePositions(events)
    onPatchedRef.current?.()
    if (schedule?.element) {
      for (const event of events) {
        applyColors(schedule.element, event, getColorsRef.current)
      }
    }
    return true
  }

  const schedulePaintRetry = (events: IPatientSchedule[], attempts = 0) => {
    if (paintRetryRafRef.current) {
      cancelAnimationFrame(paintRetryRafRef.current)
      paintRetryRafRef.current = undefined
    }
    if (paintPositions(events)) return
    if (attempts >= 30) return
    paintRetryRafRef.current = requestAnimationFrame(() => {
      schedulePaintRetry(events, attempts + 1)
    })
  }

  const syncVisiblePositions = () => {
    if (!isTabVisible()) return
    const events = dataRef.current
    if (fingerprintSchedulePositions(events) === paintedPositionsRef.current) return
    patchLiveScheduleEvents(dataSourceRef.current, events)
    for (const event of events) scheduleEventStore.set(event.id, event)
    paintPositions(events)
  }
  const syncVisiblePositionsRef = useRef(syncVisiblePositions)
  syncVisiblePositionsRef.current = syncVisiblePositions

  useEffect(() => {
    if (appliedRef.current === data) return

    const schedule = scheduleObj.current
    const prev = appliedRef.current
    appliedRef.current = data

    const fullRefresh = () => {
      scheduleEventStore.hydrate(data)
      setDataSource(data)
      schedulePaintRetry(data)
    }

    if (prev == null) {
      scheduleEventStore.hydrate(data)
      setDataSource(data)
      schedulePaintRetry(data)
      return
    }

    if (!schedule || (schedule as { isDestroyed?: boolean }).isDestroyed) {
      fullRefresh()
      return
    }

    const diff = diffScheduleEvents(prev, data)
    const changeCount = countScheduleEventChanges(diff)
    if (changeCount === 0) {
      for (const event of data) scheduleEventStore.set(event.id, event)
      return
    }

    const isWholesale =
      prev.length === 0 || changeCount > Math.max(prev.length, data.length) * WHOLESALE_CHANGE_RATIO
    if (isWholesale || needsScheduleCollectionRefresh(diff)) {
      const removedIds = diff.removed.map((event) => event.id)
      removeScheduleEventsById(dataSourceRef.current, removedIds)
      removeScheduleEngineEvents(schedule, removedIds)
      for (const id of removedIds) scheduleEventStore.remove(id)
      fullRefresh()
      return
    }

    const renderedById =
      diff.positionChanged.length > 0 ? getRenderedEventsById(schedule) : new Map()
    const alreadyPlaced = diff.positionChanged.filter((event) => {
      const current = renderedById.get(toScheduleEventId(event.id))
      return !!current && isSameSchedulePosition(current, event)
    })
    const toReposition = diff.positionChanged.filter(
      (event) =>
        !alreadyPlaced.some(
          (placed) => toScheduleEventId(placed.id) === toScheduleEventId(event.id),
        ),
    )

    const updates = [...diff.contentChanged, ...diff.positionChanged]
    const { missingIds } = patchLiveScheduleEvents(dataSourceRef.current, updates)
    if (missingIds.length > 0) {
      fullRefresh()
      return
    }

    patchScheduleEngineEvents(schedule, updates)
    for (const event of data) scheduleEventStore.set(event.id, event)

    const contentUpdates = [...diff.contentChanged, ...alreadyPlaced]
    if (schedule.element) {
      for (const event of contentUpdates) {
        applyColors(schedule.element, event, getColorsRef.current)
      }
    }

    if (alreadyPlaced.length > 0 && toReposition.length === 0) {
      paintedPositionsRef.current = fingerprintSchedulePositions(data)
    }

    if (toReposition.length > 0) {
      if (!paintPositions(data)) {
        return
      }
    }
  }, [data, scheduleObj])

  useEffect(() => {
    const onVisible = () => syncVisiblePositionsRef.current()
    document.addEventListener('visibilitychange', onVisible)
    window.addEventListener('focus', onVisible)
    return () => {
      document.removeEventListener('visibilitychange', onVisible)
      window.removeEventListener('focus', onVisible)
      if (paintRetryRafRef.current) cancelAnimationFrame(paintRetryRafRef.current)
    }
  }, [])

  return dataSource
}
